


// Settings, built from the backend's own config schema (phase 2 brief, item
// 7) rather than hand-written forms — the same principle the nav already
// follows for the feature registry: the source of truth lives on the
// backend, and the UI walks whatever it declares instead of a maintained
// parallel list.
//
// REAL SHAPE (read live from GET /api/config/schema, 2026-09-22):
//
//   { fields: [ {path:"server.host", type, default, value, description,
//                secret_ref, choices}, ... ],
//     dynamic_keyed_sections: { transports: {path, description,
//                item_fields:[...], value:{...}} },
//     list_sections: [ {path:"observers.sources", description,
//                item_fields:[...], value:[...]}, ... ] }
//
// `fields` is a flat list; each entry's dotted `path` is "<section>.<key>"
// — sections are derived by grouping on the first path segment, not a
// nested object. Each entry already carries its live `value`, so no
// separate "current config" fetch is needed. PATCH /api/config/{section}
// takes {patch: {<key>: <newValue>, ...}} scoped to that top-level section
// name (confirmed via PatchRequest in the backend's OpenAPI schema).
//
// LIST SECTIONS AND DYNAMIC-KEYED SECTIONS BOTH CARRY REAL LIVE VALUES
// (corrected 2026-09-21, extended 2026-09-22): every list_sections entry
// carries a populated `value` array, and every dynamic_keyed_sections entry
// now carries a populated `value` OBJECT keyed by instance id (config_schema.
// py's schema_as_api_dict deliberately shapes dynamic_keyed_sections
// identically to list_sections — path/description/
// item_fields/value — so both kinds of section share one rendering rule).
// The rule actually implemented here: a section is editable iff its `value`
// is present as the right container type (`Array.isArray(...)` for a list
// section, a plain object for a dynamic-keyed one); otherwise it renders as
// a plain read-only reference. This is generic, never a hardcoded list of
// section names, so a future section of either kind picks up the correct
// behaviour automatically the moment the backend starts returning its
// `value`.
//
// SAVE CONTRACT for a list section (backend/amalgamesh/config.py's
// update_section + config_schema.top_level_list_sections — verified, not
// guessed): PATCH /api/config/{section} only ever resolves a TOP-LEVEL
// section name. top_level_list_sections() is literally `{ls.path for ls in
// LIST_SCHEMA if "." not in ls.path}` — i.e. ONLY "regions" has no dot and
// resolves directly. Every other list section's path is dotted
// ("mqtt.brokers", "observers.sources", ...) and has no top-level config
// key of that exact dotted name at all — PATCHing "/api/config/mqtt.brokers"
// would not resolve to anything. So saving a dotted list section targets
// its OWN top-level segment ("mqtt") and nests the list under the
// remaining path inside the patch body ({"brokers": [...]}), relying on
// the backend's deep-merge to replace that nested list wholesale — see
// listSectionSaveTarget() below, implemented generically (split on the
// first ".") rather than as a hardcoded six-way switch.
//
// SAVE CONTRACT for a dynamic-keyed section (transports today): the section
// path IS already the top-level config key, and each instance is its own
// key one level down, so the patch body is {patch: {<instance_id>:
// {<field>: <value>, ...}, ...}} — _deep_merge on the backend merges each
// named instance's changed fields in place, leaving every other instance
// and every unmentioned field of a changed instance untouched. _deep_merge
// can ADD a new top-level key (so adding an instance works this way too)
// but can never DELETE one — see DynamicKeyedSectionEditor below for what
// that means for "removing" an instance.
//
// Secrets: `secret_ref: true` marks a field whose live value is never sent
// to the client — the schema itself doesn't even echo it in `value`. The
// UI renders it blank with a placeholder and only includes it in a PATCH if
// the admin actually types a new value.
//
// Choices: `choices` (non-empty only for a handful of str fields, e.g.
// transports' `kind`) lists the fixed set of values a field accepts. When
// present, FieldEditor renders a <select> over those raw values rather than
// a free-text input — the option text is a human label from
// CONFIG_CHOICE_LABEL (definitions.ts), keyed by the raw value; the stored
// value itself is always the schema's own literal string, never the label.
//
// Labels: every section/field name rendered on this page comes from
// lib/definitions.ts's configSectionLabel()/configFieldLabel() registries
// (never a locally re-derived humanize()) — see that file's own header
// comment for the registry's scope and rules. Category grouping comes from
// the same file's configSectionCategory()/CONFIG_CATEGORIES.
//
// APP SHELL (2026-09-21 rebuild): the page used to be a flat stack of ~25
// accordion bars — every section rendered permanently, collapsed, in one
// long column. Replaced with a real settings-app layout: a persistent left
// category menu, a tab strip when a category holds more than one section,
// and a content pane for the selected section. There is no expand/collapse
// chevron anywhere in this navigation — RadioConfigSection keeps its own
// internal accordion (that component is out of scope for this rebuild and
// is rendered as-is), but nothing this file owns uses one. The one
// disclosure this file DOES render — "Optional details" inside a list
// section's record editor — is deliberately the sole exception; see the
// master-detail record editor section below.
//
// MASTER-DETAIL RECORD EDITOR: one component (ListSectionEditor) used by
// all six list sections uniformly — regions is no longer special-cased,
// and its old dedicated /api/regions fetch is gone; every list section now
// reads its live items straight off the schema's own `value` array, the
// same way every flat field already reads `value`/`default`. One record is
// shown in the detail pane at a time (never every record's full field set
// stacked vertically), which is what keeps a section with several dozen
// fields per record from turning into a multi-thousand-pixel scroll.
//
// Search (2026-09-21, reworked): one query box, built at this page level
// from the same schema fetch, matching path/label/description across every
// flat field, every list-section item field, and every dynamic-keyed
// field — same coverage as before (201 field definitions as of this
// schema). It moved from a page-wide filter (which hid every non-matching
// section entirely) to a corner search-box + results dropdown: activating
// a result switches to the right category/tab and scrolls/highlights the
// field, without ever collapsing the menu or tabs into a flat list.
//
// Saving applies live: the PATCH/DELETE response carries `applied`, one
// entry per thing the backend put into effect ({path, result, detail}). The
// save line reads "Applied", or lists the needs-attention details verbatim
// (lib/applyResult.ts). There is no restart notion anywhere on this page.

export interface SchemaField {
  path: string;
  type?: string;
  default?: unknown;
  value?: unknown;
  description?: string;
  secret_ref?: boolean;
  // Non-empty only for a str field with a fixed, known set of values (e.g.
  // transports' `kind`). See this file's header comment on Choices.
  choices?: string[];
  // Where the Connections panel shows a transport field (see config_schema.py FieldSpec.group).
  group?: string;
}


interface ListSection {
  path: string;
  description?: string;
  item_fields?: SchemaField[];
  // Populated for every list section that actually reports live instances
  // (all 6 as of 2026-09-21). Absent (not merely empty) means "this section
  // describes an item shape, not any live instances" — see
  // ListSectionEditor/ReferenceOnlySection below for how that distinction
  // actually drives which one renders.
  value?: Record<string, unknown>[];
}


// Same shape as ListSection, deliberately (see config_schema.py's
// schema_as_api_dict, 2026-09-22) — a dynamic-keyed section's items are a
// dict keyed by instance id rather than a list, so `value` is a Record
// rather than an array; every other field means exactly the same thing.
interface DynamicKeyedSection {
  path: string;
  description?: string;
  item_fields?: SchemaField[];
  // Keyed by instance id (e.g. "meshtastic_vnode"), each value that
  // instance's own resolved settings dict. Absent means "this section
  // describes an item shape, not any live instances" — same convention as
  // ListSection.value.
  value?: Record<string, Record<string, unknown>>;
}


export interface ConfigSchemaResponse {
  fields: SchemaField[];
  dynamic_keyed_sections?: Record<string, DynamicKeyedSection>;
  list_sections?: ListSection[];
}


export function splitPath(path: string): { section: string; key: string } {
  const idx = path.indexOf(".");
  return idx === -1 ? { section: path, key: path } : { section: path.slice(0, idx), key: path.slice(idx + 1) };
}


// The measured audit constants are nested under the existing top-level
// `audit` config section but intentionally live in Advanced in the UI.
export function scalarSectionKey(path: string): string {
  return path.startsWith("audit.advanced.") ? "audit.advanced" : splitPath(path).section;
}


export function hasNestedValue(value: Record<string, unknown>, path: string): boolean {
  const segments = path.split(".");
  let cursor: unknown = value;
  for (const segment of segments) {
    if (!cursor || typeof cursor !== "object" || !(segment in cursor)) return false;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return true;
}


export function getNestedValue(value: Record<string, unknown>, path: string): unknown {
  let cursor: unknown = value;
  for (const segment of path.split(".")) {
    if (!cursor || typeof cursor !== "object") return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}


export function setNestedValue(value: Record<string, unknown>, path: string, nextValue: unknown): Record<string, unknown> {
  const segments = path.split(".");
  const result: Record<string, unknown> = { ...value };
  let cursor = result;
  for (const segment of segments.slice(0, -1)) {
    const child = cursor[segment];
    cursor[segment] = child && typeof child === "object" && !Array.isArray(child) ? { ...child as Record<string, unknown> } : {};
    cursor = cursor[segment] as Record<string, unknown>;
  }
  cursor[segments[segments.length - 1]] = nextValue;
  return result;
}


// A value counts as "set"/"present" for the primary-vs-secondary field
// split (ListSectionEditor) and for the flat-field default fallback below:
// present and neither "" nor an empty array. `false`/`0` DO count as set —
// only genuinely absent/empty values don't.
export function isFieldValueSet(v: unknown): boolean {
  return v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
}


export function fieldDomId(sectionKey: string, path: string): string {
  return `settings-field--${encodeURIComponent(sectionKey)}--${encodeURIComponent(path)}`;
}
